"use client";

import type { ReactNode } from "react";
import type { Theme, SxProps } from "@mui/material/styles";
import type { AppButtonProps } from "src/components/button";

import { memo, forwardRef } from "react";

import Box from "@mui/material/Box";

import { AppButton } from "src/components/button";

import { mergeSx } from "../../utils/merge-sx";

/**
 * Alinhamento horizontal do helper em cima do botão.
 *  - `start` — alinhado ao início do botão.
 *  - `center` — centralizado.
 *  - `end` — alinhado ao fim do botão (default — segue o padrão "↵ Enter para continuar"
 *    que aparece sempre na ponta direita do CTA primário).
 */
export type ButtonHelperAlign = "start" | "center" | "end";

export type ButtonProps = AppButtonProps & {
  /**
   * Texto/elemento auxiliar exibido **acima** do botão.
   *
   * Padrão de uso: dicas de atalho ("↵ Enter para continuar"), labels de
   * contexto ("Etapa 3/8"), ou avisos curtos ("Última chance"). Não pega
   * eventos de mouse (decorativo).
   *
   * Aceita `string` ou qualquer node — passe `<>↵ <strong>Enter</strong></>` etc.
   * Quando `undefined`, o botão renderiza como antes (sem overhead).
   */
  helper?: ReactNode;
  /** Default: `"end"`. */
  helperAlign?: ButtonHelperAlign;
  /**
   * Em qual breakpoint o helper aparece.
   *  - `"always"` (default): sempre visível.
   *  - `"smUp"`: escondido no mobile, aparece em `sm+`. Use pra dicas de
   *    teclado (Enter, atalhos) que não fazem sentido no touch.
   */
  helperVisibility?: "always" | "smUp";
  /** Override pontual de estilo do helper. */
  helperSx?: SxProps<Theme>;
  /**
   * Texto que substitui `children` enquanto `loading` está ativo. Útil pra
   * dar contexto da ação ("Salvando…", "Enviando…", "Validando…") em vez
   * de manter o label original (que pode soar agressivo, ex.: "EXCLUIR"
   * com spinner).
   *
   * O spinner do `AppButton` já aparece à esquerda por default — esse prop
   * só troca o texto. Quando `undefined`, o `children` original fica.
   */
  loadingText?: ReactNode;
};

// ============================================================================
// Estilos
// ============================================================================

function getHelperPositionSx(align: ButtonHelperAlign): SxProps<Theme> {
  const horizontal =
    align === "start"
      ? { left: 0 }
      : align === "center"
        ? { left: "50%", transform: "translateX(-50%)" }
        : { right: 0 };
  return {
    position: "absolute",
    bottom: "calc(100% + 6px)",
    ...horizontal,
    fontSize: 11,
    lineHeight: 1.2,
    // Token do tema (não branco hardcoded): antes o helper sumia em qualquer
    // superfície clara. `text.disabled` mantém o tom bem discreto em light/dark.
    color: "text.disabled",
    textAlign: align === "start" ? "left" : align === "center" ? "center" : "right",
    whiteSpace: "nowrap",
    pointerEvents: "none",
  } as SxProps<Theme>;
}

// ============================================================================
// Componente
// ============================================================================

/**
 * **Button** — botão canônico da plataforma.
 *
 * Implementado em cima do `AppButton` (`src/components/button`) — herda
 * todos os tones (`primary`, `pro`, `whatsapp`, ...), variantes
 * (`contained`/`outlined`/`text`), estado de loading via `LoadingButton`,
 * `pill`, etc.
 *
 * **Extras do DS** (não existem no `AppButton`):
 *
 *  - **`helper`** — slot pra texto auxiliar exibido **acima** do botão.
 *    Tipicamente usado pra dicas de atalho ("↵ Enter para continuar") ou
 *    pequenos avisos. Não captura eventos, renderiza fora do botão (sem
 *    afetar a área clicável).
 *  - **`helperAlign`** (`start` | `center` | `end`, default `end`) controla
 *    o alinhamento horizontal do helper.
 *  - **`helperVisibility`** (`always` | `smUp`, default `always`) — útil
 *    pra esconder dicas de teclado no mobile, onde não fazem sentido.
 *
 * Quando `helper` é `undefined`, o componente renderiza apenas o
 * `AppButton` (sem wrapper extra) — zero overhead pro caso comum.
 *
 * @example Com dica de atalho (Enter)
 * ```tsx
 * <Button
 *   type="submit"
 *   tone="primary"
 *   helper={<>↵ Enter para continuar</>}
 *   helperAlign="end"
 *   helperVisibility="smUp"
 * >
 *   CONTINUAR
 * </Button>
 * ```
 *
 * @example Com aviso curto
 * ```tsx
 * <Button helper="Última chance" helperAlign="center" tone="error">
 *   Cancelar inscrição
 * </Button>
 * ```
 */
const ButtonImpl = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    helper,
    helperAlign = "end",
    helperVisibility = "always",
    helperSx,
    loadingText,
    loading,
    loadingPosition,
    children,
    ...buttonProps
  },
  ref
) {
  // Swap do label quando há `loadingText` e o botão está em loading.
  // Mantemos `children` quando `loadingText` está ausente — preserva o
  // comportamento atual de TODOS os callsites existentes.
  const resolvedChildren = loading && loadingText != null ? loadingText : children;

  // Padrão de loading do botão canônico: durante o `loading`, escondemos o
  // label e mostramos SÓ o spinner (centralizado, mantendo a largura medida
  // pelo label — sem "pulo"/encolhimento). Exceção: quando há `loadingText`
  // (feedback textual explícito, ex.: "Salvando…"), mantemos o texto ao lado
  // do spinner (`start`). Um `loadingPosition` explícito do callsite vence.
  const resolvedLoadingPosition = loadingPosition ?? (loadingText != null ? "start" : "center");

  if (helper == null) {
    return (
      <AppButton
        ref={ref}
        loading={loading}
        loadingPosition={resolvedLoadingPosition}
        {...buttonProps}
      >
        {resolvedChildren}
      </AppButton>
    );
  }

  const helperBaseSx = getHelperPositionSx(helperAlign);
  const visibilitySx: SxProps<Theme> =
    helperVisibility === "smUp" ? { display: { xs: "none", sm: "block" } } : {};

  const { sx: buttonSx, fullWidth, ...restButtonProps } = buttonProps;

  const wrapperWidthSx: SxProps<Theme> =
    helperVisibility === "smUp"
      ? { width: { xs: "100%", sm: fullWidth ? "100%" : "auto" } }
      : { width: fullWidth ? "100%" : "auto" };

  return (
    <Box sx={{ position: "relative", display: "inline-flex", ...wrapperWidthSx }}>
      <Box component="span" aria-hidden sx={mergeSx(helperBaseSx, visibilitySx, helperSx)}>
        {helper}
      </Box>
      <AppButton
        ref={ref}
        loading={loading}
        loadingPosition={resolvedLoadingPosition}
        fullWidth={fullWidth}
        sx={mergeSx(
          helperVisibility === "smUp" ? { width: { xs: "100%", sm: "auto" } } : null,
          buttonSx
        )}
        {...restButtonProps}
      >
        {resolvedChildren}
      </AppButton>
    </Box>
  );
});

export const Button = memo(ButtonImpl);
Button.displayName = "Button";

export type {
  AppButtonTone as ButtonTone,
  AppButtonSize as ButtonSize,
} from "src/components/button";
